import {fetchAuthSession} from 'aws-amplify/auth';
import {cognitoUserPoolsTokenProvider} from 'aws-amplify/auth/cognito';

/**
 * The portal's session, refreshed against Cognito immediately before a code is
 * minted from it (FNP-871, FNP-1358).
 *
 * Two things the mint sites need that a plain `fetchAuthSession()` does not give:
 *
 * - PROOF THE SESSION IS STILL ALIVE. Amplify answers from local storage until
 *   the access token expires, so a session revoked elsewhere (another app's
 *   sign-out revokes the shared refresh token) still looks valid here for up to
 *   an hour. Minting from it hands the app tokens that die at its first refresh.
 *   Forcing the refresh asks Cognito instead.
 * - THE REFRESH TOKEN. Without it the app cannot refresh at all and its session
 *   ends when the access token does, at 60 minutes. That is FNP-1358. It is read
 *   from Amplify's own token store, never by parsing localStorage keys, whose
 *   names are Amplify's private format.
 *
 * ⚠️ NEVER LOG the return value. It carries all three raw tokens.
 */
export type FreshSession =
    | {
    readonly status: 'fresh';
    readonly idToken: string;
    readonly accessToken: string;
    readonly refreshToken: string;
}
    // Nothing stored: the user is signed out. Not a failure.
    | { readonly status: 'no-session' }
    // A session was stored but Cognito would not refresh it. The caller must
    // send the user to sign in, never mint.
    | { readonly status: 'expired' }
    // The refresh failed for a transient reason and the session is intact.
    // The caller must not mint, and must not send the user to sign in either:
    // signing in again does not fix a network error.
    | { readonly status: 'unavailable' };

const tokenStore = () => cognitoUserPoolsTokenProvider.authTokenStore;

/**
 * Local only: no revocation. Revoking here would end the shared session for
 * every other app, and the refresh has already shown this one is unusable.
 *
 * Only called once the session is known to be dead. The sign-in page reloads
 * on a cleared store, so it shows the form instead of trying to mint again.
 */
const expire = async (): Promise<FreshSession> => {
    await tokenStore().clearTokens();
    return {status: 'expired'};
};

export const loadFreshSession = async (): Promise<FreshSession> => {
    if (await tokenStore().loadTokens() === null) {
        return {status: 'no-session'};
    }

    let tokens;
    try {
        ({tokens} = await fetchAuthSession({forceRefresh: true}));
    } catch {
        // Never log the error: it can carry the request that held the token.
        //
        // Amplify decides whether this was fatal: it clears the store when the
        // tokens are definitively invalid, and keeps them on a transient error
        // (network, throttling, or the identity pool, which a forced refresh
        // also reaches). Clearing them ourselves would sign the user out of the
        // portal over a blip.
        return await tokenStore().loadTokens() === null
            ? {status: 'expired'}
            : {status: 'unavailable'};
    }

    // A refused refresh resolves with no tokens rather than throwing.
    if (!tokens?.idToken || !tokens?.accessToken) {
        return expire();
    }

    const refreshToken = (await tokenStore().loadTokens())?.refreshToken;
    if (!refreshToken) {
        return expire();
    }

    return {
        status: 'fresh',
        idToken: tokens.idToken.toString(),
        accessToken: tokens.accessToken.toString(),
        refreshToken,
    };
};
